import { useCallback, useEffect, useRef, useState } from "react";
import { assistantService } from "../../api/assistantService";
import { apiErrorMessage } from "../../api/taskBoardService";
import {
  STORED_LIMIT,
  activeDraft,
  buildHistory,
  messageText,
  restoreMessages,
  supersedePending,
} from "./assistantText";

export const ERROR_TEXT = "I couldn't reach the assistant right now. Try again in a moment.";
const TYPE_INTERVAL_MS = 45;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const load = (key) => {
  try {
    return restoreMessages(JSON.parse(localStorage.getItem(key)));
  } catch {
    return [];
  }
};

/**
 * Conversation state for the task assistant. The backend is stateless, so the
 * conversation lives here (localStorage, per user) and is sent with each call.
 * A task is created only by `confirm`, which the user triggers explicitly.
 */
export default function useAssistantChat(userId) {
  const storageKey = `abs-assistant-chat:${userId || "anon"}`;
  const [messages, setMessagesState] = useState(() => load(storageKey));
  const [typing, setTyping] = useState(false); // waiting for the model
  const [tw, setTw] = useState(null); // typewriter { id, shown }
  const ref = useRef(messages);
  const timer = useRef(null);
  const busy = useRef(false);
  const gen = useRef(0); // bumped by New chat so a late reply is dropped

  const setMessages = useCallback((next) => {
    ref.current = typeof next === "function" ? next(ref.current) : next;
    setMessagesState(ref.current);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages.slice(-STORED_LIMIT)));
    } catch {
      /* storage full or blocked: the chat still works, it just isn't kept */
    }
  }, [messages, storageKey]);

  const stopTyping = useCallback(() => {
    clearInterval(timer.current);
    setTw(null);
  }, []);
  useEffect(() => () => clearInterval(timer.current), []);

  const typeOut = useCallback(
    (id, full) => {
      if (reducedMotion()) return;
      clearInterval(timer.current);
      const tokens = full.split(/(\s+)/);
      let i = 0;
      setTw({ id, shown: "" });
      timer.current = setInterval(() => {
        i += 2;
        if (i >= tokens.length) stopTyping();
        else setTw({ id, shown: tokens.slice(0, i).join("") });
      }, TYPE_INTERVAL_MS);
    },
    [stopTyping],
  );

  const send = useCallback(
    async (text) => {
      const message = (text || "").trim();
      if (!message || busy.current) return;
      busy.current = true;
      const mine = gen.current;
      stopTyping();
      const before = ref.current;
      const history = buildHistory(before);
      const current = activeDraft(before);
      setMessages([...before, { id: `u${Date.now()}`, role: "user", kind: "text", content: message }]);
      setTyping(true);
      try {
        const res = await assistantService.chat({
          message,
          history,
          draft: current?.draft || null,
        });
        if (mine !== gen.current) return;
        const data = res.data;
        const id = `a${Date.now()}`;
        if (data.type === "draft") {
          setMessages((m) => [
            ...supersedePending(m),
            { id, role: "assistant", kind: "draft", content: data.message, draft: data.draft, draftToken: data.draftToken, status: "pending" },
          ]);
        } else {
          setMessages((m) => [...m, { id, role: "assistant", kind: "text", content: data.message }]);
          typeOut(id, data.message);
        }
      } catch (err) {
        if (mine !== gen.current) return;
        console.error("Assistant chat failed:", err);
        setMessages((m) => [...m, { id: `e${Date.now()}`, role: "assistant", kind: "error", err: true, content: ERROR_TEXT }]);
      } finally {
        if (mine === gen.current) {
          setTyping(false);
          busy.current = false;
        }
      }
    },
    [setMessages, stopTyping, typeOut],
  );

  /** Drop the answer at `index` and ask again with the user message before it. */
  const retry = useCallback(
    (index) => {
      const kept = ref.current.slice(0, index);
      const last = kept[kept.length - 1];
      if (!last || last.role !== "user") return;
      stopTyping();
      setMessages(kept.slice(0, -1));
      send(last.content);
    },
    [send, setMessages, stopTyping],
  );

  const patch = useCallback(
    (id, changes) => setMessages((m) => m.map((x) => (x.id === id ? { ...x, ...changes } : x))),
    [setMessages],
  );

  /** Create the task for a draft the user confirmed. Resolves to true on success. */
  const confirm = useCallback(
    async (id) => {
      const msg = ref.current.find((m) => m.id === id);
      if (!msg || msg.status !== "pending") return false;
      patch(id, { status: "creating", error: null });
      try {
        const res = await assistantService.confirm({ draft: msg.draft, draftToken: msg.draftToken });
        const { id: taskId, task } = res.data;
        patch(id, { status: "created", task: { id: taskId, number: task?.number, title: task?.title } });
        return true;
      } catch (err) {
        patch(id, { status: "pending", error: apiErrorMessage(err, "Could not create the task") });
        return false;
      }
    },
    [patch],
  );

  const reset = useCallback(() => {
    gen.current += 1;
    stopTyping();
    setTyping(false);
    busy.current = false;
    setMessages([]);
  }, [setMessages, stopTyping]);

  return { messages, typing, tw, send, retry, confirm, reset, messageText };
}
